import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, GripVertical, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import NewMessageForm from "@/components/messages/NewMessageForm";
import ConversationsList from "@/components/messages/ConversationsList";
import MessageThread from "@/components/pages/messages/MessageThread";
import { useMessageDialog } from "@/context/MessageDialogContext";
import { useDismissOnOutsideClick } from "@/hooks/useDismissOnOutsideClick";
import { getDisplayName } from "@/components/pages/messages/ConversationListItem";

interface DraggableMessageDialogProps {
  onClose: () => void;
}

interface Position {
  x: number;
  y: number;
}

const DEFAULT_WIDTH = 360;

export default function DraggableMessageDialog({ onClose }: DraggableMessageDialogProps) {
  const {
    registerCloseHandler,
    triggerPosition,
    pendingRecipient,
    activeConversation,
    setActiveConversation,
    isComposing,
    setIsComposing,
    openRequestId,
  } = useMessageDialog();

  const [position, setPosition] = useState<Position>(() => {
    const saved = localStorage.getItem("messageDialogPosition");
    if (saved) {
      return JSON.parse(saved);
    }
    return { x: 300, y: 120 };
  });

  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });

  const dialogRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    setIsClosing(true);
  }, []);

  useDismissOnOutsideClick(dialogRef, handleClose, !isClosing);

  const handleAnimationEnd = useCallback(() => {
    if (isClosing) {
      onClose();
    }
  }, [isClosing, onClose]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('[data-draggable="false"]')) {
      return;
    }
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      const maxX = window.innerWidth - DEFAULT_WIDTH;
      const maxY = window.innerHeight - 200;
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleBack = () => {
    if (activeConversation) {
      setActiveConversation(null);
      return;
    }
    setIsComposing(false);
  };

  useEffect(() => {
    registerCloseHandler(handleClose);
    return () => registerCloseHandler(null);
  }, [registerCloseHandler, handleClose]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      localStorage.setItem("messageDialogPosition", JSON.stringify(position));
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [position]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragOffset, position]);

  useEffect(() => {
    setIsClosing(false);
  }, [openRequestId]);

  useEffect(() => {
    if (pendingRecipient) {
      setActiveConversation(null);
      setIsComposing(true);
    }
  }, [pendingRecipient, setActiveConversation]);

  const participantNumber = activeConversation?.participants[0]?.phoneNumber;
  const fromLabel = activeConversation?.fromPhoneNumber?.alias || activeConversation?.fromPhoneNumber?.number;
  const isSingleTask = Boolean(activeConversation) || isComposing;

  return (
    <div
      ref={dialogRef}
      className={`fixed bg-background border rounded-lg shadow-2xl z-50 select-none flex flex-col overflow-hidden ${isClosing ? "dialog-exit" : "dialog-enter"}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${DEFAULT_WIDTH}px`,
        maxHeight: `calc(100vh - ${position.y}px)`,
        cursor: isDragging ? "grabbing" : "default",
        transformOrigin: triggerPosition
          ? `${triggerPosition.x - position.x}px ${triggerPosition.y - position.y}px`
          : "0% 50%",
      }}
      onAnimationEnd={handleAnimationEnd}
    >
      <div
        className="flex flex-shrink-0 cursor-grab items-center justify-between gap-2 border-b bg-background px-3 py-2.5 active:cursor-grabbing"
        onMouseDown={handleMouseDown}
      >
        {isSingleTask ? (
          <div className="flex min-w-0 items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={handleBack}
              data-draggable="false"
              title="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            {activeConversation ? (
              <div className="min-w-0" data-draggable="false">
                <p className="truncate text-[13px] font-bold dark:text-white">
                  {getDisplayName(activeConversation)}
                </p>
                <p className="truncate text-[11px] text-muted-foreground">
                  {participantNumber}
                  {fromLabel ? ` · via ${fromLabel}` : ""}
                </p>
              </div>
            ) : (
              <span className="text-[13px] font-bold dark:text-white">New message</span>
            )}
          </div>
        ) : (
          <div className="flex min-w-0 items-center gap-2">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
            <span className="text-[13px] font-bold dark:text-white">Messages</span>
          </div>
        )}

        <div className="flex shrink-0 items-center gap-1.5">
          {!isSingleTask && (
            <Button size="sm" className="h-7 text-xs" onClick={() => setIsComposing(true)} data-draggable="false">
              New message
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleClose}
            data-draggable="false"
            title="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* min-h-0 below is load-bearing: a flex item defaults to min-height:auto, so without it the
          body cannot shrink below the height of the whole message list, the dialog overflows its
          maxHeight, and overflow-hidden clips the composer out of view. */}
      {activeConversation ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden" data-draggable="false">
          <MessageThread conversation={activeConversation} showFromBar={false} />
        </div>
      ) : isComposing ? (
        <div className="flex-1 overflow-y-auto p-4" data-draggable="false">
          <NewMessageForm />
        </div>
      ) : (
        <div className="flex-1 overflow-hidden p-2" data-draggable="false">
          <ConversationsList />
        </div>
      )}
    </div>
  );
}
